import api from './api';
import { getImpersonatorId } from '@/utils/impersonationClaim';
import {
  QUESTION_OPTIONS_STORAGE_VERSION,
  type ManualPlacementEntry,
  type QuestionOptionsStateRecord,
} from '@/types';

const createDefaultState = (): QuestionOptionsStateRecord => ({
  options: {},
  manualPlacements: {},
  updatedAt: new Date().toISOString(),
  version: QUESTION_OPTIONS_STORAGE_VERSION,
});

const sanitizeManualPlacement = (
  entry: ManualPlacementEntry
): ManualPlacementEntry => ({
  ...entry,
  updatedAt: entry.updatedAt || new Date().toISOString(),
  mode: entry.mode ?? 'general',
});

export const sanitizeQuestionOptionsState = (
  raw: Partial<QuestionOptionsStateRecord> | null | undefined
): QuestionOptionsStateRecord => {
  if (!raw) return createDefaultState();

  const sanitized: QuestionOptionsStateRecord = {
    options: {},
    manualPlacements: {},
    updatedAt: raw.updatedAt ?? new Date().toISOString(),
    version: raw.version ?? QUESTION_OPTIONS_STORAGE_VERSION,
  };

  if (raw.options && typeof raw.options === 'object' && !Array.isArray(raw.options)) {
    for (const [key, value] of Object.entries(raw.options)) {
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        const entries = Object.entries(value).filter(([, option]) =>
          option === null || typeof option === 'string' || typeof option === 'boolean' ||
          (typeof option === 'number' && Number.isFinite(option))
        );
        sanitized.options = { ...sanitized.options, [key]: Object.fromEntries(entries) };
      }
    }
  }

  if (raw.manualPlacements) {
    Object.entries(raw.manualPlacements).forEach(([key, value]) => {
      if (
        value &&
        typeof value === 'object' &&
        typeof value.passageId === 'string' &&
        typeof value.questionType === 'string' &&
        typeof value.difficulty === 'string' &&
        typeof value.text === 'string'
      ) {
        sanitized.manualPlacements = {
          ...sanitized.manualPlacements,
          [key]: sanitizeManualPlacement(value as ManualPlacementEntry),
        };
      }
    });
  }

  return sanitized;
};

export async function loadQuestionOptionsState(): Promise<QuestionOptionsStateRecord> {
  const response = await api.get('/api/user-options');
  const state = (response.data && response.data.state) || null;
  const sanitized = sanitizeQuestionOptionsState(state);

  if (sanitized.version !== QUESTION_OPTIONS_STORAGE_VERSION) {
    sanitized.version = QUESTION_OPTIONS_STORAGE_VERSION;
    sanitized.updatedAt = new Date().toISOString();
  }

  return sanitized;
}

const draftKey = (userId: number) => `autoqm-question-options-draft:${userId}`;

export function readQuestionOptionsDraft(userId: number | undefined): QuestionOptionsStateRecord | undefined {
  if (userId === undefined) return undefined;
  try {
    const raw = localStorage.getItem(draftKey(userId));
    return raw ? sanitizeQuestionOptionsState(JSON.parse(raw)) : undefined;
  } catch { return undefined; }
}

export function writeQuestionOptionsDraft(userId: number | undefined, state: QuestionOptionsStateRecord): void {
  if (userId === undefined) return;
  try { localStorage.setItem(draftKey(userId), JSON.stringify(state)); } catch { /* In-memory edits still save. */ }
}

// Providers remount on navigation. Keep ordering outside the provider so an
// older PUT cannot finish after the next route's newer PUT and undo edits.
let writeQueue: Promise<void> = Promise.resolve();

export async function saveQuestionOptionsState(
  state: QuestionOptionsStateRecord,
  userId?: number,
): Promise<void> {
  const token = localStorage.getItem('auth_token');
  // An admin acting as another user never writes the user's options; the local draft is
  // kept so nothing is marked as acknowledged. The server refuses the write with 403 too.
  if (token && getImpersonatorId(token) != null) return;
  const write = writeQueue.catch(() => {}).then(async () => {
    if (!token || localStorage.getItem('auth_token') !== token) {
      throw new Error('로그인 상태가 변경되었습니다. 다시 로그인한 뒤 저장해주세요.');
    }
    const payload: QuestionOptionsStateRecord = {
      ...state,
      updatedAt: new Date().toISOString(),
      version: QUESTION_OPTIONS_STORAGE_VERSION,
    };

    await api.put('/api/user-options', { state: payload });
    if (userId !== undefined) {
      try {
        if (localStorage.getItem(draftKey(userId)) === JSON.stringify(state)) localStorage.removeItem(draftKey(userId));
      } catch { /* Storage may be disabled. */ }
    }
  });
  writeQueue = write;
  return write;
}

export async function clearQuestionOptionsState(): Promise<void> {
  await writeQueue.catch(() => {});
  const token = localStorage.getItem('auth_token');
  if (token && getImpersonatorId(token) != null) return;
  await api.delete('/api/user-options');
}
